import React, { useEffect } from 'react'
import styled, { css } from 'styled-components'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { isBefore } from 'date-fns'

import { _ } from '../../../utils/localization'
import { ParamSlug } from '../../../types'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import styles from '../../../constants/styles'
import EmbeddedVideo from '../../embeddedvideo/EmbeddedVideo'
import { toDate } from '../../../utils/datetime'
import useInterval from '../../../hooks/useInterval'
import ActionBar from '../components/ActionBar'
import Questions from '../components/Questions'
import Countdown from '../components/Countdown'
import {
    fetchPresentationArea,
    fullScreenSelector,
    presentationSelector,
    showPublicChatSelector,
    showSidebarSelector, showWidgetSelector
} from '../presentationSlice'
import { RetrievePresentationSpaceResponse } from '../../../types/__generated_swagger'
import Sidebar from '../components/Sidebar'
import { useJoinPresentation } from '../subscriptions'
import Widget from '../components/Widget'
import useHelpContent from '../../../hooks/useHelpContent'
import PublicChatBar from '../../../components/PublicChatBar'

const S = {
    PresentationRoom: styled.div<{ backgroundImage: string }>`
        background-image: url(${({ backgroundImage }) => backgroundImage || ''});
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center;
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
    `,

    Presentation: styled.div<{isFullScreen?: boolean, minHeight: number}>`
        margin: ${({ isFullScreen }) => (isFullScreen ? 'auto' : '10px auto 50px auto')};
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    `,

    VideoPlayerWrapper: styled.div<{ $width: number, $height: number }>`
        background-color: #2d3035;
        border-radius: 4px 4px 0 0;
        overflow: hidden;
        width: ${({ $width }) => $width}px;
        height: ${({ $height }) => $height}px;
        justify-content: center;
        align-content: center;
        display: flex;
    `,

    QuestionsWrapper: styled.div<{videoWidth: number, isFullScreen: boolean}>`
        width: ${({ videoWidth }) => videoWidth}px;
        margin-top: 5px;
        ${({ isFullScreen }) => isFullScreen && css`
            position: absolute;
            top: 80px;
            max-width: 800px;
            width: 80vw;
        `}
    `,

    NothingScheduled: styled.div`
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
    `,
}

const NothingScheduled = () => (
    <S.NothingScheduled>
        <h1>{_('presentation.no_presentation_scheduled')}</h1>
    </S.NothingScheduled>
)

const WatchTime = ({ data }: { data: RetrievePresentationSpaceResponse }) => {
    const upcoming = data.upcoming_presentation
    const current = data.current_presentation
    const { presentationSlug, eventSlug } = useParams<ParamSlug>()
    const time = useInterval()
    const dispatch = useDispatch()

    const newPresentationStarted = upcoming
        && isBefore(toDate(upcoming.start_date), toDate(time))

    const presentationFinished = current
        && isBefore(toDate(current.end_date), toDate(time))

    const shouldUpdatePresentation = newPresentationStarted || presentationFinished

    useEffect(() => {
        if (shouldUpdatePresentation) {
            dispatch(fetchPresentationArea({
                presentationSlug,
                eventSlug,
            }))
        }
    }, [dispatch, eventSlug, presentationSlug, shouldUpdatePresentation])
    return null
}

const PresentationRoom = () => {
    const { presentationSlug, eventSlug } = useParams<ParamSlug>()
    useJoinPresentation(presentationSlug)
    const dispatch = useDispatch()
    const { data, status } = useSelector(presentationSelector(presentationSlug))
    const { height: windowHeight, width: windowWidth } = useWindowDimensions()
    let videoHeight = Math.min(windowHeight - styles.topNavHeight - 230, 575) // 230 is height of action bar + question wrapper + Presentation's top-bot margin
    let videoWidth = videoHeight * (16 / 9)

    if (videoWidth + 500 > windowWidth) {
        videoWidth = windowWidth - 500 // Add 250px left-right side for sponsor
        videoHeight = videoWidth * (9 / 16)
    }

    const isShowWidget = useSelector(showWidgetSelector)
    const isShowSidebar = useSelector(showSidebarSelector)
    const isShowPublicChat = useSelector(showPublicChatSelector)
    const isFullScreen = useSelector(fullScreenSelector())
    if (isFullScreen) {
        videoHeight = window.innerHeight - styles.topNavHeight
        videoWidth = window.innerWidth
    }
    const currentPresentation = data && data.current_presentation
    const upcomingPresentation = data && data.upcoming_presentation
    const hasPresentation = !!currentPresentation
    const hasUpcomingPresentation = !!upcomingPresentation
    const isCountdown = !hasPresentation && !!hasUpcomingPresentation
    const presentation = hasPresentation ? currentPresentation : upcomingPresentation
    const isNothingScheduled = !hasPresentation && !hasUpcomingPresentation
    const chatId = data?.meta.chat_id
    const hasData = !!data

    useHelpContent(data?.meta.help_content)

    useEffect(() => {
        // TODO see if we can remove this if statement
        if (!hasData) {
            dispatch(fetchPresentationArea({
                presentationSlug,
                eventSlug,
            }))
        }
    }, [dispatch, presentationSlug, eventSlug, hasData])

    if (status === 'loading' || !data) {
        return null
    }

    return (
        <S.PresentationRoom backgroundImage={data.meta.image}>
            {!data.meta.image && _('presentation.no_background_image')}
            <S.Presentation isFullScreen={isFullScreen} minHeight={videoHeight}>
                <S.VideoPlayerWrapper $width={videoWidth} $height={videoHeight}>
                    {hasPresentation && <EmbeddedVideo url={data.current_stream} />}
                    {isNothingScheduled && <NothingScheduled />}
                    {isCountdown && <Countdown datetime={data.upcoming_presentation?.start_date} />}
                    { data && <WatchTime data={data} /> }
                </S.VideoPlayerWrapper>

                <ActionBar
                    presentation={presentation}
                    videoWidth={videoWidth}
                    isFullScreen={isFullScreen}
                    isShowSidebar={isShowSidebar || isShowWidget || isShowPublicChat}
                    chatId={chatId}
                />
                <S.QuestionsWrapper videoWidth={videoWidth} isFullScreen={isFullScreen}>
                    <Questions />
                </S.QuestionsWrapper>
            </S.Presentation>
            {isShowSidebar && <Sidebar isShowSidebar={isShowSidebar} isFullScreen={isFullScreen} />}
            <Widget isShowWidget={isShowWidget} presentation={presentation} />
            <PublicChatBar
                isShowPublicChat={isShowPublicChat}
                chatId={chatId}
                header={_('presentation.public_chat.title')}
            />
        </S.PresentationRoom>
    )
}

export default PresentationRoom
